import {
  ContentLibraryMediaPlayer,
  ContentLibraryMediaPlayerRemotePayload,
  MediaPlayerRemoteInteractionType,
} from 'features/content-library/types';

type PlayerUpdates = Partial<
  Pick<ContentLibraryMediaPlayer, 'state' | 'volume' | 'muted' | 'currentTime'>
>;

const TYPE_FIELDS: Record<MediaPlayerRemoteInteractionType, ReadonlyArray<keyof PlayerUpdates>> = {
  playback: ['state', 'currentTime'],
  volume: ['volume', 'currentTime'],
  mute: ['muted', 'currentTime'],
  seek: ['currentTime'],
};

/**
 * Builds the player field updates to merge for an inbound payload.
 *
 * mode='live': retain only the fields the sender's `type` discriminator claims.
 *   Bug C: prevents a non-volume broadcast from propagating a volume:0 transient.
 *
 * mode='snapshot': apply all non-null fields regardless of `payload.type`.
 *   The server stamps `type: 'playback'` on snapshots; it carries no live intent.
 *
 * Null guards on `state`/`volume`/`muted` defend against server null-injection.
 * `currentTime` uses `!== undefined` because 0 is a valid seek target.
 */
export const buildPlayerUpdates = (
  payload: ContentLibraryMediaPlayerRemotePayload,
  mode: 'live' | 'snapshot'
): PlayerUpdates => {
  const updates: PlayerUpdates = {};

  if (payload.state != null) {
    updates.state = payload.state;
  }
  if (payload.volume != null) {
    updates.volume = payload.volume;
  }
  if (payload.muted != null) {
    updates.muted = payload.muted;
  }
  if (payload.currentTime !== undefined) {
    updates.currentTime = payload.currentTime;
  }

  if (mode === 'snapshot') {
    return updates;
  }

  // mode === 'live': retain only the keys the sender's type discriminator claims.
  const retain = new Set<keyof PlayerUpdates>(TYPE_FIELDS[payload.type]);
  const filtered: PlayerUpdates = {};
  for (const key of retain) {
    if (key in updates) {
      // @ts-expect-error — key/value relation is known by the TYPE_FIELDS map
      filtered[key] = updates[key];
    }
  }
  return filtered;
};
